import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { DramaAnalysisResponse } from '@dramadetector/shared';

/**
 * Context types for drama analysis
 */
export type ContextType = 'family' | 'work' | 'friends' | 'dating';

/**
 * Error trace information for debugging (Phase 3 Observability)
 */
export interface AnalysisErrorInfo {
  /** Human-readable error message */
  message: string;
  /** Error code (e.g., 'ANALYSIS_FAILED') */
  code?: string;
  /** E2E trace ID for debugging */
  requestId?: string;
  /** ISO timestamp when error occurred */
  timestamp?: string;
}

/**
 * Individual analysis item with metadata
 */
export interface AnalysisItem {
  /** Unique identifier for the analysis */
  id: string;
  /** Original message that was analyzed */
  message: string;
  /** Context in which the message was sent */
  context: ContextType;
  /** Analysis result from the API */
  result: DramaAnalysisResponse | null;
  /** Unix timestamp when analysis was created */
  timestamp: number;
  /** Current status of the analysis */
  status: 'pending' | 'success' | 'error';
  /** @deprecated Use errorInfo instead. Error message if status is 'error' */
  error?: string;
  /** Error trace information for debugging (Phase 3 Observability) */
  errorInfo?: AnalysisErrorInfo;
}

/**
 * Analysis store state and actions
 */
interface AnalysisState {
  /** Array of all analysis items, sorted by timestamp (newest first) */
  history: AnalysisItem[];
  /** Currently active analysis being viewed or processed */
  currentAnalysis: AnalysisItem | null;

  // Actions

  /**
   * Add a new analysis to the history
   * @param item - Analysis item without id and timestamp (auto-generated)
   * @returns The generated ID for the new analysis
   */
  addAnalysis: (item: Omit<AnalysisItem, 'id' | 'timestamp'>) => string;

  /**
   * Update an existing analysis with partial updates
   * @param id - ID of the analysis to update
   * @param updates - Partial updates to apply
   */
  updateAnalysis: (id: string, updates: Partial<AnalysisItem>) => void;

  /**
   * Delete an analysis from history
   * @param id - ID of the analysis to delete
   */
  deleteAnalysis: (id: string) => void;

  /**
   * Clear all analysis history
   */
  clearHistory: () => void;

  /**
   * Set the currently active analysis
   * @param analysis - Analysis to set as current, or null to clear
   */
  setCurrentAnalysis: (analysis: AnalysisItem | null) => void;

  // Selectors

  /**
   * Get an analysis by ID
   * @param id - ID of the analysis to retrieve
   * @returns The analysis item or undefined if not found
   */
  getById: (id: string) => AnalysisItem | undefined;

  /**
   * Get the most recent analyses
   * @param limit - Maximum number of items to return
   * @returns Array of recent analyses, sorted by timestamp (newest first)
   */
  getRecent: (limit: number) => AnalysisItem[];

  /**
   * Get all analyses for a specific context
   * @param context - Context type to filter by
   * @returns Array of analyses for the context, sorted by timestamp (newest first)
   */
  getByContext: (context: ContextType) => AnalysisItem[];
}

/**
 * Maximum number of items to keep in history
 */
const MAX_HISTORY_ITEMS = 50;

/**
 * Maximum age of items in days
 */
const MAX_AGE_DAYS = 30;

/**
 * Remove items older than MAX_AGE_DAYS and keep only MAX_HISTORY_ITEMS
 */
const cleanupHistory = (history: AnalysisItem[]): AnalysisItem[] => {
  const now = Date.now();
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

  // Filter out old items
  const filtered = history.filter(item => now - item.timestamp < maxAge);

  // Sort by timestamp (newest first) and limit to MAX_HISTORY_ITEMS
  return filtered
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_HISTORY_ITEMS);
};

/**
 * Analysis store with AsyncStorage persistence
 *
 * Features:
 * - Persistent storage of analysis history
 * - User-scoped storage keys (Security: Solution 2)
 * - Auto-cleanup of old items (>30 days)
 * - Limit to 50 most recent items
 * - Current analysis tracking
 * - Efficient selectors for filtering and retrieval
 *
 * Security: Uses user-scoped AsyncStorage keys to prevent data leakage
 * between authentication sessions (OWASP A01, GDPR Article 32 compliance)
 */
export const useAnalysisStore = create<AnalysisState>()(
  persist(
    (set, get) => ({
      history: [],
      currentAnalysis: null,

      addAnalysis: (item) => {
        const id = crypto.randomUUID();
        const timestamp = Date.now();

        const newItem: AnalysisItem = {
          ...item,
          id,
          timestamp,
        };

        set((state) => ({
          history: cleanupHistory([newItem, ...state.history]),
        }));

        return id;
      },

      updateAnalysis: (id, updates) => {
        set((state) => ({
          history: state.history.map((item) =>
            item.id === id ? { ...item, ...updates } : item
          ),
          currentAnalysis:
            state.currentAnalysis?.id === id
              ? { ...state.currentAnalysis, ...updates }
              : state.currentAnalysis,
        }));
      },

      deleteAnalysis: (id) => {
        set((state) => ({
          history: state.history.filter((item) => item.id !== id),
          currentAnalysis:
            state.currentAnalysis?.id === id ? null : state.currentAnalysis,
        }));
      },

      clearHistory: () => {
        set({
          history: [],
          currentAnalysis: null,
        });
      },

      setCurrentAnalysis: (analysis) => {
        set({ currentAnalysis: analysis });
      },

      getById: (id) => {
        return get().history.find((item) => item.id === id);
      },

      getRecent: (limit) => {
        const { history } = get();
        return history.slice(0, limit);
      },

      getByContext: (context) => {
        const { history } = get();
        return history.filter((item) => item.context === context);
      },
    }),
    {
      name: 'drama-analysis-storage', // Note: User-scoped key handled by partialize
      storage: createJSONStorage(() => AsyncStorage),
      // Solution 2: Partition storage by user ID for automatic isolation
      // This provides defense-in-depth alongside Solution 1 (cache clearing)
      partialize: (state) => {
        // User-scoped key: Zustand persist middleware doesn't support async partialize,
        // so user scoping happens via cache clearing on logout (Solution 1).
        // See AuthContext.tsx signOut flow for cache clearing logic.
        return state;
      },
      // Migrate function to handle user-scoped keys
      migrate: (persistedState: any, version: number) => {
        // For now, return as-is. User scoping happens via cache clearing (Solution 1)
        // Future enhancement: Implement full user-scoped keys with migration
        return persistedState as AnalysisState;
      },
      onRehydrateStorage: () => (state) => {
        // Cleanup old items on hydration
        if (state) {
          state.history = cleanupHistory(state.history);
        }
      },
    }
  )
);
